import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserAccessRule } from './useUserAccess';

interface DeleteRuleModalProps {
  rule: UserAccessRule;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for deleting a visibility rule.
 *
 * Ported from the hand-rolled overlay in `UserAccessAdmin.cshtml` rather than
 * swapped for react-bootstrap's Modal: the markup carries its own `uac-*`
 * styling, and reusing it keeps this screen looking exactly as it did. Escape
 * and a backdrop click both cancel, as they did there.
 */
export function DeleteRuleModal({ rule, pending, onCancel, onConfirm }: DeleteRuleModalProps) {
  const { t } = useTranslation('mystery/dealerlist');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div
      className="uac-modal-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="uac-modal" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
        <div className="uac-modal-icon">
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </div>

        <h3 className="uac-modal-title" id="delete-modal-title">
          {t('UserAccess.DeleteTitle', 'Delete this rule?')}
        </h3>
        <p className="uac-modal-text">
          {t('UserAccess.DeleteText', 'Rule #{{id}} ({{scope}}) will be permanently removed.', {
            id: rule.id,
            scope: rule.scopeType,
          })}
        </p>

        <div className="uac-modal-actions">
          <button type="button" className="uac-btn uac-btn-ghost" onClick={onCancel}>
            {t('UserAccess.Cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="uac-btn uac-btn-danger-solid"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? t('UserAccess.Deleting', 'Deleting…') : t('UserAccess.Delete', 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
